import React, { useState } from 'react';
import Select from 'react-select';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import './BusinessHours.css';

export const BUSINESSHOURS = [
  { value: 0, label: '12 AM' },
  { value: 1, label: '1 AM' },
  { value: 2, label: '2 AM' },
  { value: 3, label: '3 AM' },
  { value: 4, label: '4 AM' },
  { value: 5, label: '5 AM' },
  { value: 6, label: '6 AM' },
  { value: 7, label: '7 AM' },
  { value: 8, label: '8 AM' },
  { value: 9, label: '9 AM' },
  { value: 10, label: '10 AM' },
  { value: 11, label: '11 AM' },
  { value: 12, label: '12 PM' },
  { value: 13, label: '1 PM' },
  { value: 14, label: '2 PM' },
  { value: 15, label: '3 PM' },
  { value: 16, label: '4 PM' },
  { value: 17, label: '5 PM' },
  { value: 18, label: '6 PM' },
  { value: 19, label: '7 PM' },
  { value: 20, label: '8 PM' },
  { value: 21, label: '9 PM' },
  { value: 22, label: '10 PM' },
  { value: 23, label: '11 PM' }
];

const customSelectStyle = {
  control: (provided) => ({
    ...provided,
    width: '80px',
    minHeight: '28px',
    cursor: 'pointer',
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
    boxShadow: 'none',
    backgroundColor: '#f8fafc',
    '&:hover': {
      borderColor: '#3b5998',
      backgroundColor: '#ffffff'
    }
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '160px',
    border: '1px solid #e1e5e9',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  }),
  valueContainer: (provided) => ({
    ...provided,
    fontSize: '11px',
    padding: '4px 6px'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#3b5998'
      : state.isFocused
      ? '#f1f5f9'
      : 'white',
    color: state.isSelected ? 'white' : '#374151',
    cursor: 'pointer',
    padding: '6px 8px',
    fontSize: '11px'
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1000,
    width: '80px'
  })
};

const Schedule = ({
  dayName,
  values,
  setFieldValue,
  errors,
  touched,
  dayIndex
}) => {
  const [startDate, setStartDate] = useState(
    values?.businessHours[dayName.toLowerCase()]?.split('/')[0] || '9'
  );
  const [endDate, setEndDate] = useState(
    values?.businessHours[dayName.toLowerCase()]?.split('/')[1] || '18'
  );

  const currentHours = values?.businessHours[dayName.toLowerCase()];
  const isClosed = currentHours === '/' || !currentHours;

  // Debug: log current values
  console.log(`Schedule for ${dayName}:`, {
    startDate,
    endDate,
    isClosed,
    values: values?.businessHours[dayName.toLowerCase()]
  });

  const toggleState = () => {
    if (isClosed) {
      setFieldValue(`businessHours.${dayName.toLowerCase()}`, '9/18');
    } else {
      setFieldValue(`businessHours.${dayName.toLowerCase()}`, '/');
    }
  };

  // Use simple styles like the original component
  const selectorStyles = customSelectStyle;

  return (
    <div className="schedule-item">
      <div className="schedule-content">
        <div className="day-info">
          <span className="day-name">{dayName}</span>
        </div>

        <div className="time-controls">
          <div className="time-selector">
            <Select
              options={BUSINESSHOURS}
              isSearchable={false}
              value={BUSINESSHOURS.find((bh) => bh.value === Number(startDate))}
              onChange={(selectedOption) => {
                console.log('Start time changed:', selectedOption);
                setStartDate(selectedOption.value);
                setFieldValue(
                  `businessHours.${dayName.toLowerCase()}`,
                  `${selectedOption.value}/${endDate}`
                );
              }}
              styles={selectorStyles}
              placeholder="Open"
              isDisabled={isClosed}
            />
          </div>

          <span className="time-separator">-</span>

          <div className="time-selector">
            <Select
              options={BUSINESSHOURS}
              isSearchable={false}
              value={BUSINESSHOURS.find((bh) => bh.value === Number(endDate))}
              onChange={(selectedOption) => {
                setEndDate(selectedOption.value);
                setFieldValue(
                  `businessHours.${dayName.toLowerCase()}`,
                  `${startDate}/${selectedOption.value}`
                );
              }}
              styles={selectorStyles}
              placeholder="Close"
              isDisabled={isClosed}
            />
          </div>

          <button
            type="button"
            className={`toggle-button ${isClosed ? 'closed' : 'open'}`}
            onClick={toggleState}
            title={isClosed ? 'Set as Open' : 'Set as Closed'}>
            {isClosed ? 'Closed' : 'Open'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BusinessHours = ({ values, setFieldValue, errors, touched }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const days = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  const openDays = days.filter(
    (day) =>
      values?.businessHours[day.toLowerCase()] &&
      values?.businessHours[day.toLowerCase()] !== '/'
  ).length;

  const summaryText = `${openDays} days open`;

  return (
    <div className="business-hours-dropdown">
      <div
        className="business-hours-header"
        onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-content">
          <h6 className="header-title">Business Hours</h6>
          <span className="header-summary">{summaryText}</span>
        </div>
        <div className="header-toggle">
          {isExpanded ? (
            <ChevronUpIcon className="chevron-icon" />
          ) : (
            <ChevronDownIcon className="chevron-icon" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="business-hours-content">
          {days.map((day, index) => (
            <Schedule
              key={day}
              dayName={day}
              values={values}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              dayIndex={index}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BusinessHours;
